import React from 'react';

/**
 * PageSkeleton
 * Shown inside <Suspense fallback={<PageSkeleton />}> while a lazy page chunk
 * is downloading. Mimics a typical CRM page layout so there is zero blank-screen
 * flash and no layout shift (CLS = 0).
 */

const shimmer = `
  @keyframes shimmer {
    0%   { background-position: -800px 0; }
    100% { background-position:  800px 0; }
  }
`;

function Bone({ width = '100%', height = 16, radius = 6, style = {} }) {
  return (
    <div
      style={{
        width,
        height,
        borderRadius: radius,
        background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
        backgroundSize: '800px 100%',
        animation: 'shimmer 1.4s infinite linear',
        ...style,
      }}
    />
  );
}

export default function PageSkeleton() {
  return (
    <>
      {/* Inject keyframes once */}
      <style>{shimmer}</style>

      <div style={styles.page}>
        {/* Page header row */}
        <div style={styles.header}>
          <Bone width={220} height={28} radius={8} />
          <div style={styles.headerRight}>
            <Bone width={90} height={34} radius={8} />
            <Bone width={110} height={34} radius={8} />
          </div>
        </div>

        {/* Stats / KPI cards */}
        <div style={styles.cards}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} style={styles.card}>
              <Bone width="55%" height={14} style={{ marginBottom: 12 }} />
              <Bone width="35%" height={28} radius={6} style={{ marginBottom: 8 }} />
              <Bone width="70%" height={12} />
            </div>
          ))}
        </div>

        {/* Main content area — table-like rows */}
        <div style={styles.table}>
          {/* Table header */}
          <div style={styles.tableHead}>
            {[30, 18, 14, 14, 12].map((w, i) => (
              <Bone key={i} width={`${w}%`} height={12} />
            ))}
          </div>

          {/* Table rows */}
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} style={{ ...styles.tableRow, opacity: 1 - i * 0.1 }}>
              <div style={styles.rowCell}>
                <Bone width={32} height={32} radius={16} style={{ flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <Bone width="80%" height={13} style={{ marginBottom: 6 }} />
                  <Bone width="55%" height={11} />
                </div>
              </div>
              <Bone width="15%" height={13} />
              <Bone width="12%" height={22} radius={20} />
              <Bone width="10%" height={13} />
              <Bone width="8%"  height={13} />
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

const styles = {
  page: {
    padding: '24px 28px',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 28,
    flexWrap: 'wrap',
    gap: 12,
  },
  headerRight: {
    display: 'flex',
    gap: 10,
  },
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 16,
    marginBottom: 28,
  },
  card: {
    background: '#fff',
    border: '1px solid #edf2f7',
    borderRadius: 10,
    padding: '18px 16px',
  },
  table: {
    background: '#fff',
    border: '1px solid #edf2f7',
    borderRadius: 10,
    overflow: 'hidden',
  },
  tableHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '14px 20px',
    borderBottom: '1px solid #edf2f7',
    background: '#f7fafc',
  },
  tableRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '14px 20px',
    borderBottom: '1px solid #f7fafc',
  },
  rowCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flex: '0 0 30%',
  },
};